// KanCraft shop phone line (jobq-api "SHOP PHONE LINE", shop_phone.py; since 2026-10-23).
//
// (620) 235-5883 rings Jesse's desk phone. From the Shop Phone page (pages/ShopPhonePage.jsx) an Admin
// marks a caller:
// - spam: the caller gets a busy signal (no ring, no voicemail, no email);
// - priority: the desk AND Jesse's cell ring together, at any hour (the cell hears "press 1" first, so his
//   own voicemail can't take the call).
// This module is the ONE copy of the reads, writes and wording. Every call is securityFetch (the
// session's Admin PIN): jobq-api's /phone/shop/calls and /phone/shop/numbers take X-Admin-Pin.

import { securityFetch } from '../../utils/securityApi';
import { shopClock } from '../../utils/shopDate';

export const CALL_DAYS = 14;
export const CALLS_POLL_MS = 30000;

export const fetchShopCalls = () => securityFetch(`/phone/shop/calls?days=${CALL_DAYS}`);

/** kind: 'spam' | 'priority' | null (clear). The API normalizes the number and answers it E.164. */
export const markNumber = (number, kind, label) =>
  securityFetch('/phone/shop/numbers', {
    method: 'PUT',
    body: JSON.stringify({ number, kind, label: label || null }),
  });

/** '+13165551234' -> '(316) 555-1234'; anything else as given ('' for nothing). */
export function prettyNumber(e164) {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(String(e164 || '').trim());
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : String(e164 || '');
}

export const MARKS = Object.freeze({
  spam: { label: 'Spam', what: 'Gets a busy signal: no ring, no voicemail.', tone: 'bad' },
  priority: { label: 'Priority', what: "Rings the desk and Jesse's cell, any hour.", tone: 'ok' },
});

export const markInfo = (kind) => MARKS[kind] || null;

/** What happened on a call, for the call list: {text, tone}. */
export function callOutcome(call) {
  if (!call) return { text: '', tone: 'muted' };
  if (call.direction === 'outbound') return { text: 'Called from the desk', tone: 'muted' };
  if (call.dial_status === 'blocked') return { text: 'Blocked (spam)', tone: 'bad' };
  if (call.dial_status === 'completed') {
    return { text: call.caller_kind === 'priority' ? 'Answered (desk or cell)' : 'Answered', tone: 'ok' };
  }
  const vm = call.voicemail_state;
  if (vm === 'emailed') return { text: 'Voicemail, emailed', tone: 'warn' };
  if (vm === 'failed') return { text: 'Voicemail, email FAILED', tone: 'bad' };
  if (vm === 'pending' || vm === 'sending') return { text: 'Voicemail, sending', tone: 'warn' };
  if (vm === 'too_short') return { text: 'Hung up at the beep', tone: 'muted' };
  if (call.after_hours && !call.dial_status) return { text: 'After hours, no message', tone: 'muted' };
  return { text: 'Missed, no message', tone: 'muted' };
}

/** One call's line under the caller: when, plus the caller ID name Twilio sent. */
export function callWhen(call) {
  const parts = [shopClock(call.started_at)];
  if (call.after_hours && call.direction === 'inbound') parts.push('after hours');
  if (call.caller_name) parts.push(call.caller_name);
  return parts.filter(Boolean).join(' · ');
}

/** Confirm text before a mark is saved (spam silences a caller for good, so it always asks). */
export function markConfirmText(kind, number, label) {
  const who = label ? `${label} ${prettyNumber(number)}` : prettyNumber(number);
  if (kind === 'spam') return `Block ${who} as spam? They will get a busy signal from now on.`;
  if (kind === 'priority') return `Make ${who} a priority caller? Their calls will ring Jesse's cell too, any hour.`;
  return `Clear the mark on ${who}? Their calls ring the desk like anyone else's.`;
}

/** The ?number= from a voicemail email's link, as the API wants it (or null). */
export function numberFromSearch(search) {
  try {
    const v = new URLSearchParams(search || '').get('number');
    return v && v.trim() ? v.trim() : null;
  } catch (_) {
    return null;
  }
}
